import type { ConsentRequest } from "@fiket/booth-schema";
import { unwrapEnvelope } from "../../lib/apiEnvelope";

/**
 * 매장 Wi-Fi 가 멈추면 POST 가 영원히 안 끝나 손님이 스피너에 갇힌다(재시도까지 두 번) — 시도당 10초 상한을 두고
 * 실패로 떨어뜨려 §4.3 의 "동의 증적만 들고 진행" 폴백이 돌게 한다. 결제(`features/Payment/paymentApi.ts`) 와 같은 규칙.
 */
const REQUEST_TIMEOUT_MS = 10_000;

function withTimeout(callerSignal?: AbortSignal): AbortSignal {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(new DOMException("Request timed out", "TimeoutError")), REQUEST_TIMEOUT_MS);
  const abort = () => { clearTimeout(timer); controller.abort(); };
  if (callerSignal) {
    if (callerSignal.aborted) { clearTimeout(timer); controller.abort(); }
    else callerSignal.addEventListener("abort", abort, { once: true });
  }
  return controller.signal;
}

/**
 * 동의 제출 `POST /api/booth/:slug/consent` 클라이언트 (BOOTH-66).
 * 성공 201 `{ success: true, data: { consentId } }`, 실패는 봉투 오류 → `ApiEnvelopeError` 로 throw.
 * 타임아웃·이탈(`signal`)은 `AbortError`/`TimeoutError` 로 던진다 — 재시도·폴백 정책은 호출부(ConsentView)가 쥔다.
 */
export async function postConsent(
  slug: string,
  req: ConsentRequest,
  signal?: AbortSignal
): Promise<{ consentId: string }> {
  const res = await fetch(`/api/booth/${slug}/consent`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    cache: "no-store",
    body: JSON.stringify(req),
    signal: withTimeout(signal),
  });
  return unwrapEnvelope<{ consentId: string }>(res);
}
